import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, X, Plus } from "lucide-react";
import { api, formatApiErrorDetail, fmtDateUS } from "@/lib/api";
import { PayrollGrid, periodFor } from "./PayrollGrid";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = `${inputCls} appearance-none`;

export const CrewPanel = ({ onChanged = () => {} }) => {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ name: "", pin: "", role: "employee", hourly_rate: "" });
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.role === "foreman" && !form.pin.trim()) return toast.error("The foreman needs a PIN to log in");
    if (form.role === "manager" && !form.pin.trim()) return toast.error("The manager needs a PIN to log in");
    setBusy(true);
    try {
      const payload = { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 };
      if (editId) await api.patch(`/admin/employees/${editId}`, payload);
      else await api.post("/admin/employees", payload);
      toast.success(editId ? "Employee updated" : `${form.name} added${form.pin ? ` — share their PIN: ${form.pin}` : ""}`);
      setForm({ name: "", pin: "", role: "employee", hourly_rate: "" });
      setEditId(null);
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (e) => {
    if (!window.confirm(`Remove ${e.name}? Their logged hours stay on the projects.`)) return;
    try {
      await api.delete(`/admin/employees/${e.id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="border border-primary/40 bg-primary/5 rounded-xl p-5 space-y-4" data-testid="crew-panel">
      <p className="text-xs font-bold uppercase tracking-widest text-primary">Crew — only foremen need a PIN; workers just need a name and pay rate</p>
      <div className="space-y-2">
        {employees.length === 0 && <p className="text-white/40 text-xs">No crew yet — add your foreman (with PIN) and your workers (name + pay rate) below.</p>}
        {employees.map((e) => (
          <div key={e.id} className="flex items-center gap-3 border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5" data-testid={`crew-${e.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{e.name} <span className={`text-[10px] font-bold uppercase tracking-widest ${e.is_manager ? "text-amber-300" : e.role === "foreman" ? "text-primary" : "text-white/40"}`}>{e.is_manager ? "manager" : e.role === "foreman" ? "foreman" : "worker"}</span></p>
              <p className="text-[11px] text-white/45">{e.pin ? `PIN ${e.pin}` : "no login — foreman logs their hours"} · {e.hourly_rate > 0 ? `$${e.hourly_rate}/h` : ""}{e.hours_14d > 0 ? ` · ${e.hours_14d}h last 14 days (${e.cost_14d?.toLocaleString("en-US", { maximumFractionDigits: 0 })}$)` : ""}</p>
              {e.hourly_rate <= 0 && <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300 mt-0.5" data-testid={`crew-nowage-${e.id}`}>Wage not set — add it here so payroll counts their hours</p>}
            </div>
            <button onClick={() => { setEditId(e.id); setForm({ name: e.name, pin: e.pin, role: e.is_manager ? "manager" : e.role, hourly_rate: String(e.hourly_rate) }); }} aria-label={`Edit ${e.name}`} data-testid={`crew-edit-${e.id}`} className="text-white/40 hover:text-primary p-1.5"><Pencil size={14} /></button>
            <button onClick={() => deactivate(e)} aria-label={`Remove ${e.name}`} data-testid={`crew-delete-${e.id}`} className="text-red-400/70 hover:text-red-300 p-1.5"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="crew-name" className={inputCls} />
        <input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 8) })} placeholder={form.role === "foreman" || form.role === "manager" ? "PIN (4-8 digits) *" : "PIN — only if they log in"} inputMode="numeric" data-testid="crew-pin" className={inputCls} />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="crew-role" className={selectCls}>
          <option value="employee" className="bg-[#252720]">Worker — no login needed</option>
          <option value="foreman" className="bg-[#252720]">Foreman — PIN login, runs the crew</option>
          <option value="manager" className="bg-[#252720]">Manager — foreman on ALL projects, can edit closed periods</option>
        </select>
        <input type="number" min="0" step="0.5" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="Pay rate $/h" data-testid="crew-rate" className={inputCls} />
      </div>
      <button onClick={save} disabled={busy} data-testid="crew-save" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 rounded-lg disabled:opacity-50">
        {busy ? "Saving..." : editId ? "Update Employee" : "Add Employee"}
      </button>
    </div>
  );
};

export const PayrollPanel = () => {
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [schedule, setSchedule] = useState({ anchor_date: "2026-10-11", period_days: 14 });
  const [payFrom, setPayFrom] = useState(() => periodFor("2026-10-11", 14, todayISO()).start);
  const [payTo, setPayTo] = useState(() => periodFor("2026-10-11", 14, todayISO()).end);
  const [payBusy, setPayBusy] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [dedForm, setDedForm] = useState({ label: "", minutes: "", scope: "all", date: new Date().toISOString().slice(0, 10) });
  const [dedBusy, setDedBusy] = useState(false);
  const [lunchOn, setLunchOn] = useState(false);
  const [lunchMin, setLunchMin] = useState("30");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/admin/settings/pay-schedule").then(({ data }) => {
      setSchedule(data);
      const p = periodFor(data.anchor_date, data.period_days, todayISO());
      setPayFrom(p.start);
      setPayTo(p.end);
    }).catch(() => {});
  }, []);

  const loadSummary = useCallback(() => {
    api.get("/admin/payroll/summary", { params: { from_date: payFrom, to_date: payTo } }).then(({ data }) => setSummary(data)).catch(() => {});
  }, [payFrom, payTo]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const fmtMin = (mins) => (mins >= 60 ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ""}` : `${mins}m`);

  const loadDeductions = useCallback(() => {
    api.get("/admin/settings/payroll-deductions").then(({ data }) => {
      if (!data.configured) { saveLunch(true, "30"); return; } // lunch on by default for new setups
      setDeductions(data.deductions || []);
      setLunchOn((data.lunch_minutes || 0) > 0);
      setLunchMin(String(data.lunch_minutes || 30));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadDeductions(); }, [loadDeductions]);

  const saveDeductions = async (next) => {
    setDedBusy(true);
    try {
      const { data } = await api.put("/admin/settings/payroll-deductions", {
        lunch_minutes: lunchOn ? (parseInt(lunchMin, 10) || 30) : 0,
        deductions: next,
      });
      setDeductions(data.deductions || []);
      loadSummary();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    } finally {
      setDedBusy(false);
    }
  };

  const saveLunch = async (on, mins) => {
    const minutes = on ? Math.max(5, parseInt(mins, 10) || 30) : 0;
    try {
      const { data } = await api.put("/admin/settings/payroll-deductions", { lunch_minutes: minutes, deductions });
      setLunchOn(data.lunch_minutes > 0);
      setLunchMin(String(data.lunch_minutes || 30));
      toast.success(data.lunch_minutes > 0 ? `Lunch on — ${data.lunch_minutes} min comes off every worked day automatically` : "Lunch deduction off");
      loadSummary();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addDeduction = async () => {
    if (!dedForm.label.trim()) return toast.error("Name the deduction — e.g. 'Extra break'");
    const minutes = parseInt(dedForm.minutes, 10);
    if (!minutes || minutes <= 0) return toast.error("Minutes must be greater than 0");
    const ok = await saveDeductions([...deductions, {
      id: crypto.randomUUID ? crypto.randomUUID() : `ded-${Date.now()}`,
      label: dedForm.label.trim(), minutes,
      scope: dedForm.scope, date: dedForm.scope === "date" ? dedForm.date : "",
    }]);
    if (ok) {
      toast.success("Deduction added — it applies to the payroll export");
      setDedForm({ label: "", minutes: "", scope: "all", date: new Date().toISOString().slice(0, 10) });
    }
  };

  const removeDeduction = async (id) => {
    const ok = await saveDeductions(deductions.filter((d) => d.id !== id));
    if (ok) toast.success("Deduction removed");
  };

  const downloadPayroll = async () => {
    setPayBusy(true);
    try {
      const { data } = await api.get("/admin/payroll.csv", { params: { from_date: payFrom, to_date: payTo }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll-${payFrom}-to-${payTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Payroll report downloaded — deductions already applied");
    } catch {
      toast.error("Could not build the report");
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <div className="border border-primary/40 bg-primary/5 rounded-xl p-5 space-y-4" data-testid="payroll-panel">
      <p className="text-xs font-bold uppercase tracking-widest text-primary">Payroll — review hours, then export the report</p>
      <div className="flex items-center gap-2.5 border border-primary/30 rounded-lg bg-primary/5 px-3.5 py-2.5" data-testid="lunch-deduction">
        <input type="checkbox" checked={lunchOn} onChange={(e) => saveLunch(e.target.checked, lunchMin)} aria-label="Deduct lunch" data-testid="lunch-toggle" className="w-4 h-4 accent-primary" />
        <span className="text-sm text-white/85 flex-1 font-semibold">Lunch break — deduct</span>
        <input type="number" min="5" max="120" step="5" value={lunchMin} onChange={(e) => setLunchMin(e.target.value)} onBlur={() => lunchOn && saveLunch(true, lunchMin)} disabled={!lunchOn} aria-label="Lunch minutes" data-testid="lunch-minutes" className={`${inputCls} !w-20 !py-2 text-center`} />
        <span className="text-[11px] text-white/50 shrink-0">min / day</span>
      </div>
      {lunchOn && <p className="text-[10px] text-white/35 -mt-1">On for every worked day by default — to remove it for one day, tap the "-{lunchMin}m lunch" note under that date in the grid below.</p>}
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={payFrom} onChange={(e) => setPayFrom(e.target.value)} data-testid="payroll-from" className={inputCls} />
        <input type="date" value={payTo} onChange={(e) => setPayTo(e.target.value)} data-testid="payroll-to" className={inputCls} />
      </div>

      {summary && summary.employees.length > 0 && (
        <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="payroll-summary">
          <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-white/5 text-[9px] font-bold uppercase tracking-widest text-white/40">
            <span className="col-span-4">Employee</span>
            <span className="col-span-1 text-right">Days</span>
            <span className="col-span-2 text-right">Worked</span>
            <span className="col-span-2 text-right">Deducted</span>
            <span className="col-span-1 text-right">Total</span>
            <span className="col-span-2 text-right">Pay</span>
          </div>
          {summary.employees.map((e) => (
            <div key={e.name} className="grid grid-cols-12 gap-1 px-3 py-2.5 border-t border-white/5 text-xs items-center" data-testid={`summary-row-${e.name.replace(/[^a-z0-9]+/gi, "-")}`}>
              <span className="col-span-4 font-semibold truncate">
                {e.name}
                {e.pending_hours > 0 && <span className="text-amber-300 text-[9px] font-bold uppercase tracking-widest ml-1.5">incl. {e.pending_hours}h pending</span>}
              </span>
              <span className="col-span-1 text-right text-white/60">{e.days}</span>
              <span className="col-span-2 text-right text-white/60">{e.gross_hours}h</span>
              <span className="col-span-2 text-right text-white/60">{e.deduct_minutes ? `-${fmtMin(e.deduct_minutes)}` : "—"}</span>
              <span className="col-span-1 text-right font-bold text-white/90" data-testid={`summary-net-${e.name.replace(/[^a-z0-9]+/gi, "-")}`}>{e.net_hours}h</span>
              <span className="col-span-2 text-right font-bold text-primary">${e.pay.toFixed(2)}</span>
            </div>
          ))}
          <div className="grid grid-cols-12 gap-1 px-3 py-2.5 border-t border-white/15 bg-white/5 text-xs font-bold items-center" data-testid="summary-totals">
            <span className="col-span-4 uppercase tracking-widest text-[10px] text-white/60">Everyone</span>
            <span className="col-span-1" />
            <span className="col-span-2 text-right text-white/70">{summary.totals.gross_hours}h</span>
            <span className="col-span-2" />
            <span className="col-span-1 text-right" data-testid="summary-total-net">{summary.totals.net_hours}h</span>
            <span className="col-span-2 text-right text-primary" data-testid="summary-total-pay">${summary.totals.pay.toFixed(2)}</span>
          </div>
          <p className="text-[10px] text-white/35 px-3 py-2 border-t border-white/5">Totals for {fmtDateUS(summary.from_date)} – {fmtDateUS(summary.to_date)}{summary.lunch_minutes > 0 ? ` · lunch ${summary.lunch_minutes} min/day already deducted` : ""}{summary.totals.paid_pay > 0 ? ` · $${summary.totals.paid_pay.toFixed(2)} already paid (in the ledger)` : ""}. Review and approve hours in the grid below; download the full report if you want the spreadsheet.</p>
        </div>
      )}
      {summary && summary.employees.length === 0 && (
        <p className="text-[11px] text-white/35" data-testid="summary-empty">No hours logged in this period yet.</p>
      )}
      <button onClick={downloadPayroll} disabled={payBusy} data-testid="payroll-download" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 rounded-lg disabled:opacity-50">
        {payBusy ? "Building..." : "Download CSV"}
      </button>

      <PayrollGrid
        fromDate={payFrom}
        toDate={payTo}
        schedule={schedule}
        onNavigate={(f, t) => { setPayFrom(f); setPayTo(t); }}
        onScheduleChange={setSchedule}
        onChanged={loadSummary}
      />

      <div className="border-t border-white/10 pt-4 space-y-2.5" data-testid="payroll-deductions">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">Other time deductions — taken off paid hours everywhere (summary + report)</p>
        {deductions.length === 0 && <p className="text-[11px] text-white/35">No other deductions — everyone gets paid for every logged minute.</p>}
        {deductions.map((d) => (
          <div key={d.id} className="flex items-center gap-3 border border-white/10 rounded-lg bg-background/60 px-3.5 py-2.5" data-testid={`deduction-${d.id}`}>
            <p className="flex-1 min-w-0 text-sm truncate"><span className="font-semibold">{d.label}</span> <span className="text-white/45 text-[11px]">— {d.minutes} min {d.scope === "all" ? "every day" : `on ${fmtDateUS(d.date)}`}</span></p>
            <button onClick={() => removeDeduction(d.id)} aria-label={`Remove ${d.label}`} data-testid={`deduction-remove-${d.id}`} disabled={dedBusy} className="text-red-400/70 hover:text-red-300 p-1"><X size={14} /></button>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-2">
          <input value={dedForm.label} onChange={(e) => setDedForm({ ...dedForm, label: e.target.value })} placeholder="Deduction name" data-testid="deduction-label" className={inputCls} />
          <input type="number" min="1" step="5" value={dedForm.minutes} onChange={(e) => setDedForm({ ...dedForm, minutes: e.target.value })} placeholder="Minutes" data-testid="deduction-minutes" className={inputCls} />
          <select value={dedForm.scope} onChange={(e) => setDedForm({ ...dedForm, scope: e.target.value })} data-testid="deduction-scope" className={selectCls}>
            <option value="all" className="bg-[#252720]">Every day</option>
            <option value="date" className="bg-[#252720]">One specific date</option>
          </select>
          {dedForm.scope === "date" ? (
            <input type="date" value={dedForm.date} onChange={(e) => setDedForm({ ...dedForm, date: e.target.value })} data-testid="deduction-date" className={inputCls} />
          ) : (
            <p className="text-[11px] text-white/35 self-center">Applies to every logged day</p>
          )}
        </div>
        <button onClick={addDeduction} disabled={dedBusy} data-testid="deduction-add" className="flex items-center justify-center gap-2 w-full border border-dashed border-white/25 rounded-lg py-3 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300 disabled:opacity-50">
          <Plus size={13} aria-hidden="true" /> Add deduction
        </button>
      </div>
    </div>
  );
};
